import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";

import PipelineService, { pipelineErrorMessage } from "../../services/pipeline.service";
import { IMaintenanceHandler, IPipelineStatus } from "../../types/api/pipeline.type";
import useNotifications from "../../components/NotificationProvider";
import useLoader from "../../components/LoaderProvider";
import { ConfirmationDialog } from "../../components/ConfirmationDialog";
import { ServiceStateChip } from "./common";

interface IMaintenanceTab {
  envId: string;
  status: IPipelineStatus | null;
  reloadStatus: () => Promise<void>;
}

const MaintenanceTab = ({ envId, status, reloadStatus }: IMaintenanceTab) => {
  const { setNotification } = useNotifications();
  const { setLoading: setPageLoading } = useLoader();
  const [handlers, setHandlers] = useState<IMaintenanceHandler[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [confirmStopAll, setConfirmStopAll] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await PipelineService.getMaintenanceHandlers(envId);
      setHandlers(data.handlers ?? []);
      setError(null);
    } catch (e) {
      setHandlers([]);
      setError(pipelineErrorMessage(e, "Could not load maintenance handlers"));
    } finally {
      setLoading(false);
    }
  }, [envId]);

  useEffect(() => {
    load();
  }, [load]);

  const run = async (action: () => Promise<unknown>, success: string) => {
    try {
      setPageLoading(true);
      await action();
      setNotification({ message: success, open: true });
    } catch (e) {
      setNotification({ message: pipelineErrorMessage(e), open: true, type: "error" });
    } finally {
      setPageLoading(false);
      await Promise.all([load(), reloadStatus()]);
    }
  };

  const maintenance = status?.services.maintenance;

  return (
    <Box>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="subtitle1">Maintenance service</Typography>
        <ServiceStateChip status={maintenance} />
        <Box sx={{ flexGrow: 1 }} />
        <Button
          variant="contained"
          onClick={() => run(() => PipelineService.startService(envId, "maintenance", {}), "Maintenance started")}
        >
          Start all enabled
        </Button>
        <Button color="error" onClick={() => setConfirmStopAll(true)}>
          Stop all
        </Button>
        <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
          Refresh
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table aria-label="maintenance handlers">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold" }}>Handler</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Enabled</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Running</TableCell>
                <TableCell sx={{ fontWeight: "bold" }} align="right">
                  Poll (s)
                </TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Last tick</TableCell>
                <TableCell sx={{ fontWeight: "bold" }} align="right">
                  OK / Fail
                </TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {handlers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7}>No handlers registered</TableCell>
                </TableRow>
              ) : (
                handlers.map((h) => (
                  <TableRow key={h.name} hover>
                    <TableCell>{h.name}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={h.enabled ? "enabled" : "disabled"}
                        color={h.enabled ? "success" : "default"}
                        variant={h.enabled ? "filled" : "outlined"}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={h.running ? "running" : "idle"}
                        color={h.running ? "success" : "default"}
                        variant={h.running ? "filled" : "outlined"}
                      />
                    </TableCell>
                    <TableCell align="right">{h.poll_seconds ?? "—"}</TableCell>
                    <TableCell>
                      {h.last_tick_at ? new Date(h.last_tick_at).toLocaleString() : "never"}
                      {h.last_tick_status ? ` (${h.last_tick_status})` : ""}
                    </TableCell>
                    <TableCell align="right">
                      {h.success_count ?? 0} / {h.failure_count ?? 0}
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                      <Button
                        size="small"
                        sx={{ mr: 1 }}
                        onClick={() =>
                          run(() => PipelineService.startMaintenanceHandler(envId, h.name), `${h.name} started`)
                        }
                      >
                        Start
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        onClick={() =>
                          run(() => PipelineService.stopMaintenanceHandler(envId, h.name), `${h.name} stopped`)
                        }
                      >
                        Stop
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {confirmStopAll && (
        <ConfirmationDialog
          open
          setOpen={setConfirmStopAll}
          title="Stop maintenance"
          content="Stops every maintenance handler in this tenant (ticket unsnooze, alert-board rollup, …) until started again."
          confirmationText="maintenance"
          onProceed={() => {
            setConfirmStopAll(false);
            run(() => PipelineService.stopService(envId, "maintenance"), "Maintenance stopped");
          }}
        />
      )}
    </Box>
  );
};

export default MaintenanceTab;
